import React from 'react';
import { FaFileCsv } from 'react-icons/fa';
import _ from 'lodash';
import {
  Button, FlexboxGrid, Form, InputPicker, Loader, Modal, Schema, Stack, TagPicker, Toggle,
} from 'rsuite';
import { ItemDataType } from 'rsuite/cjs/internals/types';
import CardActionImportGuide from './CardActionImportGuide';
import { GeneratedDocument } from '../api/generateddocuments/generatedDocumentsApiTypes';
import { ALLEGRO_MARKETPLACES, KAUFLAND_MARKETPLACES } from '../api/model/Marketplaces';
import { ProductStates } from '../api/apiTypes';
import { ApiGroup } from '../api/model/ApiGroup';
import { exportReport, ExportReportRequest, ReportType } from '../api/listing';
import { utils } from '../utils/utils';

const model = Schema.Model({
  report: Schema.Types.StringType().isRequired('Report must be selected', true),
  categories: Schema.Types.StringType().pattern(/^[0-9]*[;0-9]+$/, 'Categories must be a number separated by semicolon'),
  brands: Schema.Types.StringType().pattern(/^[\w ]*[;\w ]+$/, 'Brands must be separated by semicolon'),
  skus: Schema.Types.StringType().pattern(/^[\w ]*[;\w ]+$/, 'SKUs must be separated by semicolon'),
});

interface Props {
  apiGroup: ApiGroup;
  onGenerated: (document: GeneratedDocument) => void;
}

interface FormValue {
  reportType: ReportType;
  apiGroup: ApiGroup;
  categories: string;
  brands: string;
  partnerProductCodes: string;
  statuses: ProductStates[];
  marketplaceCodes: string[];
  minimumInStock: string;
  withErrors?: boolean | null;
  excludeParameters: boolean;
}

const reportTypeLabels = {
  [ReportType.NEW_PRODUCTS_FOR_LISTING]: 'New products for listing',
  [ReportType.OFFERS_WITH_MISSING_PARAMETERS]: 'Offers with missing parameters',
  [ReportType.ALL_OFFERS]: 'All offers',
};

function getReportsData(): ItemDataType[] {
  return Object
    .values(ReportType)
    .map(reportType => ({
      value: reportType,
      label: reportTypeLabels[reportType],
    }));
}

const productStatusLabels: { [key in ProductStates]?: string } = {
  [ProductStates.IMPORT_FAILED]: '(Listing) Import failed',
  [ProductStates.PUBLISHED_TEST]: 'Published test',
  [ProductStates.PUBLISHED_LIVE]: 'Published live',
  [ProductStates.UPDATE_FAILED]: 'Update failed',
  [ProductStates.ACTIVATION_FAILED]: 'Activation failed',
  [ProductStates.DRAFT]: 'MPI draft',
};

const allowedStatuses = {
  [ReportType.NEW_PRODUCTS_FOR_LISTING]: [ProductStates.IMPORT_FAILED],
  [ReportType.OFFERS_WITH_MISSING_PARAMETERS]: [
    ProductStates.PUBLISHED_TEST,
    ProductStates.PUBLISHED_LIVE,
    ProductStates.UPDATE_FAILED,
    ProductStates.ACTIVATION_FAILED,
  ],
  [ReportType.ALL_OFFERS]: [
    ProductStates.PUBLISHED_TEST,
    ProductStates.PUBLISHED_LIVE,
    ProductStates.UPDATE_FAILED,
    ProductStates.ACTIVATION_FAILED,
    ProductStates.DRAFT,
  ],
};

const marketplaces = {
  [ApiGroup.ALLEGRO]: ALLEGRO_MARKETPLACES,
  [ApiGroup.KAUFLAND]: KAUFLAND_MARKETPLACES,
  [ApiGroup.ALZA]: [],
  [ApiGroup.MALL_GROUP]: [],
};

function getAllowedStatuses(reportType: ReportType): ProductStates[] {
  return allowedStatuses[reportType] ?? [];
}

function getStatusesData(reportType: ReportType): ItemDataType[] {
  return getAllowedStatuses(reportType)
    .map(status => ({
      value: status,
      label: productStatusLabels[status],
    }));
}

const WITH_ERRORS_DATA: ItemDataType[] = [
  // @ts-ignore
  { label: 'All', value: null },
  // @ts-ignore
  { label: 'Only with errors', value: true },
  // @ts-ignore
  { label: 'Only without errors', value: false },
];

function mapExportReportRequest(formValue: FormValue): ExportReportRequest {
  return {
    ...formValue,
    categories: _.compact(_.split(formValue.categories, ';')),
    brands: _.compact(_.split(formValue.brands, ';')),
    partnerProductCodes: _.compact(_.split(formValue.partnerProductCodes, ';')),
    minimumInStock: utils.toNumberOrUndefined(formValue.minimumInStock),
  };
}

export default function DownloadOfferDataReport({ apiGroup, onGenerated }: Props) {
  const [open, setOpen] = React.useState<boolean>(false);
  const [generating, setGenerating] = React.useState<boolean>(false);
  const [formValue, setFormValue] = React.useState<FormValue>({
    reportType: ReportType.NEW_PRODUCTS_FOR_LISTING,
    apiGroup,
    categories: '',
    brands: '',
    partnerProductCodes: '',
    statuses: getAllowedStatuses(ReportType.NEW_PRODUCTS_FOR_LISTING),
    marketplaceCodes: [],
    minimumInStock: '',
    withErrors: null,
    excludeParameters: false,
  });

  function handleClose(): void {
    setOpen(false);
    setGenerating(false);
  }

  function handleReportChange(reportType: ReportType): void {
    setFormValue((prevState) => ({ ...prevState, statuses: getAllowedStatuses(reportType) }));
  }

  async function generateReport(): Promise<void> {
    setGenerating(true);

    try {
      const request = mapExportReportRequest(formValue);
      const document = await exportReport(request);
      onGenerated(document);
    } finally {
      handleClose();
    }
  }

  return (
    <div>
      <CardActionImportGuide onClick={() => setOpen(true)}>
        <FaFileCsv />
        <span>Export offer data</span>
      </CardActionImportGuide>
      <Modal
        open={open}
        backdrop="static"
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Generate report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {generating ? (
            <div style={{ textAlign: 'center' }}>
              <Loader size="md" />
            </div>
          ) : (
            <Form
              fluid
              model={model}
              formValue={formValue}
              onChange={(value) => setFormValue(value as FormValue)}
            >
              <Form.Group>
                <FlexboxGrid justify="space-between" align="top">
                  <FlexboxGrid.Item>
                    <Form.ControlLabel>
                      Report
                    </Form.ControlLabel>
                    <Form.Control
                      name="reportType"
                      placeholder="select report"
                      accepter={InputPicker}
                      data={getReportsData()}
                      onChange={(reportType: ReportType) => handleReportChange(reportType)}
                    />
                  </FlexboxGrid.Item>
                  <FlexboxGrid.Item>
                    <FlexboxGrid>
                      <Form.ControlLabel>
                        Exclude offer parameters
                      </Form.ControlLabel>
                      <Form.Control
                        name="excludeParameters"
                        checked={formValue.excludeParameters}
                        accepter={Toggle}
                      />
                    </FlexboxGrid>
                  </FlexboxGrid.Item>
                </FlexboxGrid>
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>
                  Categories
                </Form.ControlLabel>
                <Form.Control
                  name="categories"
                  placeholder="Enter categories separated by semicolon"
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>
                  Brands
                </Form.ControlLabel>
                <Form.Control
                  name="brands"
                  placeholder="Enter brands separated by semicolon"
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>
                  SKUs
                </Form.ControlLabel>
                <Form.Control
                  name="partnerProductCodes"
                  placeholder="Enter SKUs separated by semicolon"
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>
                  Statuses
                </Form.ControlLabel>
                <Form.Control
                  name="statuses"
                  placeholder="select statuses"
                  accepter={TagPicker}
                  data={getStatusesData(formValue.reportType)}
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>
                  Marketplace
                </Form.ControlLabel>
                <Form.Control
                  name="marketplaceCodes"
                  placeholder="Select marketplace"
                  accepter={TagPicker}
                  data={marketplaces[apiGroup]}
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>
                  Min stock
                </Form.ControlLabel>
                <Form.Control
                  name="minimumInStock"
                  type="number"
                  placeholder="Min stock"
                />
              </Form.Group>
              {
                formValue.reportType === ReportType.ALL_OFFERS && (
                  <Form.Group>
                    <Form.ControlLabel>
                      Errors
                    </Form.ControlLabel>
                    <Form.Control
                      name="withErrors"
                      placeholder="Select errors"
                      accepter={InputPicker}
                      data={WITH_ERRORS_DATA}
                    />
                  </Form.Group>
                )
              }
              <Stack spacing={5}>
                <Button type="submit" disabled={generating} onClick={() => generateReport()} appearance="primary">
                  Generate Report
                </Button>
                <Button disabled={generating} onClick={() => handleClose()} appearance="subtle">
                  Close
                </Button>
              </Stack>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
